'use client'

import Head from 'next/head'
import useClient from './_utils/useClient'
import { useState, useRef, useEffect } from 'react'
import InfoCard from './_components/InfoCard.js'
import { BackgroundBeams } from './_components/_ui/background-beams'
import IPhone from './_components/_ui/iphone'
import GradientButton from './_components/_ui/gradientButton'
import { useRouter } from 'next/navigation'
import { motion, useInView } from 'framer-motion'
import AlphaDialog from './_components/AlphaDialog'
import BlockSection from './_components/BlockSection'
import CreatorsSection from './_components/CreatorsSection'
import { FaImage, FaLink, FaMarkdown, FaColumns, FaCheckSquare, FaCode, FaReceipt, FaShareAlt, FaCubes, FaAdversal, FaPlusCircle } from 'react-icons/fa'
import { Space_Grotesk, Outfit } from 'next/font/google'
import Footer from './_components/_ui/Footer'
import { supabase } from './_utils/supabase'

const spaceGrotesk = Space_Grotesk({ 
  subsets: ['latin'],
  variable: '--font-space-grotesk',
  display: 'swap'
})

const outfit = Outfit({ 
  subsets: ['latin'],
  variable: '--font-outfit',
  display: 'swap'
})

const Home = () => {
    const isClient = useClient()
    const [showInfoCard, setShowInfoCard] = useState(true)
    const router = useRouter()

    useEffect(() => {
        // Handle access token in URL hash (OAuth redirects)
        const handleOAuthRedirect = async () => {
            // Check if we have an access token in the URL hash
            if (window.location.hash || window.location.hash.includes('access_token')) {
                console.log('Access token detected in URL hash, handling redirect')
                
                // Get current session using our existing supabase client
                const { data: { session } } = await supabase.auth.getSession()
                
                if (session) {
                    // User is authenticated, check if they have a username
                    if (!session.user.user_metadata?.username) {
                        console.log('Redirecting to username setup')
                        router.push('/auth/username-setup')
                    } else {
                        // User has a username, redirect to dashboard
                        const username = session.user.user_metadata.username
                        console.log('Redirecting to dashboard')
                        router.push(`/${username}/dashboard`)
                    }
                }
            }
        }
        
        if (isClient) {
            handleOAuthRedirect()
        }
    }, [isClient, router])

    const closeInfoCard = () => {
        setShowInfoCard(false)
    }

    const AnimatedBlockSection = () => {
        const blockTypes = [
            {
                id: 'image',
                icon: FaImage,
                label: 'Image',
                description: 'Add a single image or photo',
                color: 'bg-blue-500/10 text-blue-400'
            },
            {
                id: 'link',
                icon: FaLink,
                label: 'Link',
                description: 'Create link cards with rich previews',
                color: 'bg-green-500/10 text-green-400'
            },
            {
                id: 'markdown',
                icon: FaMarkdown,
                label: 'Text',
                description: 'Add formatted text with markdown',
                color: 'bg-purple-500/10 text-purple-400'
            },
            {
                id: 'kanban',
                icon: FaColumns,
                label: 'Kanban Board',
                description: 'Organize tasks with a drag-and-drop board',
                color: 'bg-yellow-500/10 text-yellow-400'
            },
            {
                id: 'checklist',
                icon: FaCheckSquare,
                label: 'Checklist',
                description: 'Create interactive checklists',
                color: 'bg-indigo-500/10 text-indigo-400'
            },
            {
                id: 'code',
                icon: FaCode,
                label: 'Code',
                description: 'Share code with syntax highlighting',
                color: 'bg-gray-500/10 text-gray-400'
            },
            {
                id: 'record',
                icon: FaReceipt,
                label: 'Record/Receipt',
                description: 'Track records, receipts, and expenses',
                color: 'bg-pink-500/10 text-pink-400'
            },
            {
                id: 'social-media',
                icon: FaShareAlt,
                label: 'Social Media',
                description: 'Add your social media profile links',
                color: 'bg-cyan-500/10 text-cyan-400'
            },
            {
                id: 'image-gallery',
                icon: FaImage,
                label: 'Image Gallery',
                description: 'Create a gallery of multiple images',
                color: 'bg-emerald-500/10 text-emerald-400'
            },
            {
                id: 'stl-viewer',
                icon: FaCubes,
                label: '3D Model Viewer',
                description: 'View 3D STL files interactively',
                color: 'bg-orange-500/10 text-orange-400'
            },
            {
                id: 'ad-block',
                icon: FaAdversal,
                label: 'Ad Block',
                description: 'Monetize your page with ads (Coming Soon)',
                color: 'bg-red-500/10 text-red-400'
            },
            {
                id: 'more',
                icon: FaPlusCircle,
                label: 'And More',
                description: 'So much more to come - stay tuned!',
                color: 'bg-purple-500/10 text-purple-400'
            }
        ]

        return (
            <>
                <div className="text-center mb-16">
                    <h2 className="font-outfit text-base text-indigo-400 font-semibold tracking-wide uppercase">
                        endless ways to share
                    </h2>
                    <p className="font-space-grotesk mt-2 text-3xl leading-[0.9] font-extrabold tracking-tight text-white sm:text-4xl uppercase
                                 [text-shadow:_0_0_10px_rgba(255,255,255,0.2),_-2px_-2px_0_#f7d032,_2px_-2px_0_#f76432,_-2px_2px_0_#32f7e1,_2px_2px_0_#7132f7]">
                        Build Your Story<br/>Block by Block
                    </p>
                    <p className="font-outfit mt-4 max-w-2xl text-xl text-white mx-auto">
                        Our intuitive block system lets you mix and match different content types.
                    </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {blockTypes.map((block, index) => (
                        <BlockCard key={block.id} block={block} index={index} />
                    ))}
                </div>
            </>
        )
    }

    const BlockCard = ({ block, index }) => {
        const ref = useRef(null)
        const isInView = useInView(ref, { once: true, amount: 0.2 })
        
        return (
            <motion.div
                ref={ref}
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-neutral-900 p-6 rounded-xl border border-neutral-800 hover:border-neutral-700 transition-all duration-300"
            >
                <div className={`flex items-center justify-center h-12 w-12 rounded-md ${block.color} mb-4`}>
                    <block.icon className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-medium text-white mb-2">{block.label}</h3>
                <p className="text-gray-400">{block.description}</p>
            </motion.div>
        )
    }

    const AutofocusVisionSection = () => {
        const ref = useRef(null)
        const isInView = useInView(ref, { once: true, amount: 0.2 })
        
        return (
            <div className="bg-white dark:bg-neutral-950 py-24 relative z-10">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                        transition={{ duration: 0.7 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-base text-purple-400 font-semibold tracking-wide uppercase">
                            The Vision
                        </h2>
                        <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-black dark:text-white sm:text-4xl">
                            Why Autofoc.us Exists
                        </p>
                    </motion.div>
                    
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                        className="prose prose-lg mx-auto dark:prose-invert text-center"
                    >
                        <div className="space-y-4">
                            <p className="text-gray-600 dark:text-gray-400">
                                I built Autofoc.us because I needed <strong>one unified space</strong> to share details about all my focuses - from <strong>Lightroom presets</strong> to <strong>4Runner mods</strong>, to <strong>camera gear</strong>, to detailed guides. No more scattered links or platforms. So I rebuilt <strong>linktree™</strong>.. but with <strong>adderall</strong> cooked in.
                            </p>
                            
                            <p className="text-gray-600 dark:text-gray-400">
                                Here's the thing: sharing information should be as simple as <strong>sharing your username</strong>. And when it's a sensitive share? Just add a <strong>password</strong>. Easy.
                            </p>
                            
                            <p className="text-gray-600 dark:text-gray-400">
                                This platform is designed for <strong>makers</strong>, <strong>creators</strong>, and <strong>content sharers</strong>. It's <strong>infinitely scalable</strong>, <strong>pluggable</strong>, and designed to keep hosting costs low on our end while delivering <strong>rich experiences</strong> for you as a viewer or creator.
                            </p>
                            
                            <p className="text-gray-600 dark:text-gray-400">
                                The goal was being able to bring your content from <strong>any platform</strong> - whether it's <strong>Imgur</strong> for images, <strong>Google Drive</strong> for files, <strong>YouTube</strong> for videos, or <strong>Instagram</strong> for short-form content. Easily combine them into one beautifully organized space with affiliate links, descriptions, and all the context your audience needs. Share it all through a <strong>single, easy-to-access link</strong> that works for any niche - from <strong>photography</strong> to <strong>cars</strong> to <strong>literally anything </strong> you're passionate about. It's the <strong>simplest way</strong> for me to create a <strong>high-value</strong>, <strong>low-maintenance</strong> hub for all your content.
                            </p>
                            
                            <p className="text-gray-600 dark:text-gray-400 font-semibold">
                                At its core is our <strong>expandable block system</strong> - the foundation that lets Autofoc.us grow with your needs, and I'm always aiming to make it better. Thanks for being a part of it.
                            </p>
                            
                            <p className="text-gray-500 dark:text-gray-400 text-sm mt-6 text-right">
                                - Nate <br/>
                                Creator of Autofoc.us
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>
        )
    }

    const AboutSectionHeading = () => {
        const ref = useRef(null)
        const isInView = useInView(ref, { once: true, amount: 0.2 })
        
        return (
            <motion.div 
                ref={ref}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.7 }}
                className="lg:text-right relative z-50"
            >
                <h2 className="text-base text-indigo-600 font-semibold tracking-wide uppercase text-right">
                    Beyond The Feed
                </h2>
                <p className="font-space-grotesk mt-2 text-3xl leading-[0.9] font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-4xl text-right uppercase
                             [text-shadow:_0_0_10px_rgba(255,255,255,0.2),_-1px_-1px_0_#f7d032,_1px_-1px_0_#f76432,_-1px_1px_0_#32f7e1,_1px_1px_0_#7132f7]">
                    Where Authenticity<br/>Meets Depth
                </p>
                <p className="mt-4 max-w-2xl text-xl text-gray-500 dark:text-gray-400 lg:ml-auto text-right ml-auto">
                    Break free from traditional social media constraints. Share your complete story, not just the highlights.
                </p>
                <p className="text-right pt-2 max-w-96 text-xs text-gray-500 dark:text-gray-400 lg:ml-auto ml-auto">
                    Share affiliate links, reveal behind-the-scenes details, and showcase your gear—all in one connected space designed for authentic creator-audience relationships.
                </p>
            </motion.div>
        )
    }

    const AboutFeature = ({ index, icon, title, description, isRightAligned, colorClass }) => {
        const ref = useRef(null)
        const isInView = useInView(ref, { once: true, amount: 0.2 })
        
        return (
            <motion.div 
                ref={ref}
                initial={{ opacity: 0, x: isRightAligned ? 30 : -30 }}
                animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: isRightAligned ? 30 : -30 }}
                transition={{ duration: 0.7, delay: index * 0.15 }}
                className={`flex ${isRightAligned ? 'justify-end' : ''}`}
            >
                <div className={`flex ${isRightAligned ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex-shrink-0 ${isRightAligned ? 'ml-4' : ''}`}>
                        <div className={`flex items-center justify-center h-12 w-12 rounded-md ${colorClass}`}>
                            {icon}
                        </div>
                    </div>
                    <div className={`${isRightAligned ? 'text-right' : 'ml-4'}`}>
                        <dt className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                            {title}
                        </dt>
                        <dd className="mt-2 text-base max-w-2xl text-gray-500 dark:text-gray-400">
                            {description}
                        </dd>
                    </div>
                </div>
            </motion.div>
        )
    }

    return (
        <>
            <AlphaDialog />
            <div className="min-h-screen w-full relative">
                <BackgroundBeams />
                <Head>
                    <title>Autofoc.us</title>
                    <link rel="icon" href="/favicon.ico" />
                </Head>
                
                <div className="relative w-full">
                    <div className="container bg-opacity-0 mx-auto relative z-40">
                        {/* Mobile: Title Section Above */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="block lg:hidden text-center pt-8"
                        >
                            <div className="relative z-10">
                                <h1 className="z-10 font-space-grotesk text-4xl sm:text-5xl uppercase font-bold text-white leading-[0.9] tracking-tighter
                                              [text-shadow:_0_0_10px_rgba(255,255,255,0.2),_-2px_-2px_0_#f7d032,_2px_-2px_0_#f76432,_-2px_2px_0_#32f7e1,_2px_2px_0_#7132f7]">
                                    Autofoc.us
                                </h1>
                                <div className="z-10 w-full relative">
                                    {/* Gradient lines - made smaller */}
                                    <div className="absolute inset-x-20 top-0 bg-gradient-to-r from-transparent via-indigo-500 to-transparent h-[1px] w-3/4 blur-sm" />
                                    <div className="absolute inset-x-20 top-0 bg-gradient-transparent via-indigo-500 to-transparent h-px w-3/4" />
                                    <div className="absolute inset-x-60 top-0 bg-gradient-to-r from-transparent via-sky-500 to-transparent h-[3px] w-1/4 blur-sm" />
                                    <div className="absolute inset-x-60 top-0 bg-gradient-to-r from-transparent via-sky-500 to-transparent h-px w-1/4" />
                                </div>
                                <p className="text-gray-300 z-50 text-lg mb-24 w-full px-4 [text-shadow:_0_0_30px_rgb(255_255_255_/_20%)]">
                                    Your dedicated space for showcasing information beyond the feed.
                                </p>
                            </div>
                        </motion.div>

                        {/* Main Content Area */}
                        <div className="bg-opacity-0 flex flex-col lg:flex-row items-center justify-center lg:justify-between min-h-screen">
                            {/* iPhone Demo Section */}
                            <motion.div
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, ease: 'easeOut' }}
                                className="w-full  lg:w-1/2 flex z-5 justify-center -mt-36 lg:pt-96"
                            >
                                <div
                                    className="transform scale-[0.45] sm:scale-[0.55] md:scale-[0.65] lg:scale-[0.75] xl:scale-[0.85] 
                               transition-transform duration-300"
                                >
                                    <IPhone />
                                </div>
                            </motion.div>

                            {/* Desktop: Content Section */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="hidden lg:block w-1/2 text-left"
                            >
                                <div className="relative z-10">
                                    <h1 className="font-space-grotesk  text-7xl xl:text-8xl uppercase font-bold text-white leading-[0.9] tracking-tighter mb-4
                                                 [text-shadow:_0_0_10px_rgba(255,255,255,0.2),_-2px_-2px_0_#f7d032,_2px_-2px_0_#f76432,_-2px_2px_0_#32f7e1,_2px_2px_0_#7132f7]">
                                        Autofoc.us
                                    </h1>
                                    <div className="relative mb-4">
                                        {/* Gradient lines */}
                                        <div className="absolute inset-x-0 top-0 bg-gradient-to-r from-transparent via-indigo-500 to-transparent h-[1px] w-3/4 blur-sm" />
                                        <div className="absolute inset-x-0 top-0 bg-gradient-to-r from-transparent via-indigo-500 to-transparent h-px w-3/4" />
                                        <div className="absolute inset-x-40 top-0 bg-gradient-to-r from-transparent via-sky-500 to-transparent h-[3px] w-1/4 blur-sm" />
                                        <div className="absolute inset-x-40 top-0 bg-gradient-to-r from-transparent via-sky-500 to-transparent h-px w-1/4" />
                                    </div>
                                    <p className="text-gray-300 z-50 text-lg mb-8 w-full max-w-xl [text-shadow:_0_0_30px_rgb(255_255_255_/_20%)]">
                                    Your dedicated space for showcasing information beyond the feed.
                                    </p>
                                </div>
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.4 }}
                                    className="flex flex-row gap-4"
                                >
                                    <GradientButton
                                        gradient="cyan-blue"
                                        text="Claim Your Username"
                                        className="animate-glow"
                                        onClick={() => router.push('/sign-up')}
                                    />
                                    <GradientButton
                                        gradient="dark"
                                        text="View Demo"
                                        onClick={() => router.push('/nate')}
                                    />
                                    <GradientButton
                                        gradient="purple-pink"
                                        text="Login"
                                        onClick={() => router.push('/login')}
                                    />
                                </motion.div>
                            </motion.div>

                            {/* Mobile: Buttons Below */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                                className="block lg:hidden w-full pt-5 pb-5 relative z-50"
                            >
                                <div className="flex flex-col sm:flex-row gap-2 justify-center items-center">
                                    <div className="w-auto">
                                        <GradientButton
                                            gradient="cyan-blue"
                                            text="Sign Up/Login"
                                            className="animate-glow"
                                            zIndex="z-50"
                                            onClick={() => router.push('/sign-up')}
                                        />
                                    </div>
                                    <div className="w-auto">
                                        <GradientButton
                                            gradient="dark"
                                            text="View Demo"
                                            zIndex="z-50"
                                            onClick={() => router.push('/nate')}
                                        />
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>

                    {/* About Section */}
                    <div className="bg-white dark:bg-gray-900 py-12 relative">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <AboutSectionHeading />

                            <div className="mt-10 relative z-40">
                                <dl className="space-y-10 md:space-y-0 md:grid md:grid-cols-1 md:gap-y-10">
                                    <AboutFeature 
                                        index={0}
                                        icon={
                                            <svg
                                                className="h-6 w-6 text-indigo-400"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                                                />
                                            </svg>
                                        }
                                        title="Curate Your Digital Universe"
                                        description="Create spaces that reflect your passions and work. From gear collections to creative processes to restaurant menus, build connections through shared interests, authentic stories, or even just engaging experiences for your audience."
                                        isRightAligned={false}
                                        colorClass="bg-indigo-500/10 text-indigo-400"
                                    />

                                    <AboutFeature 
                                        index={1}
                                        icon={
                                            <svg
                                                className="h-6 w-6 text-sky-400"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"
                                                />
                                            </svg>
                                        }
                                        title="Content That Breathes"
                                        description="Break free from character limits and carousel constraints. Your content deserve room to expand, whether it's a gear review or the journey behind your creative process."
                                        isRightAligned={true}
                                        colorClass="bg-sky-500/10 text-sky-400"
                                    />

                                    <AboutFeature 
                                        index={2}
                                        icon={
                                            <svg
                                                className="h-6 w-6 text-purple-400"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
                                                />
                                            </svg>
                                        }
                                        title="Mindful Content Architecture"
                                        description="Build with intent using our block system. Transform your simple ideas into beautiful, organized spaces that respect both creator and audience time. No algorithms, just authentic connection."
                                        isRightAligned={false}
                                        colorClass="bg-purple-500/10 text-purple-400"
                                    />

                                    <AboutFeature 
                                        index={3}
                                        icon={
                                            <svg
                                                className="h-6 w-6 text-emerald-400"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                                />
                                            </svg>
                                        }
                                        title="Your Space, Your Rules"
                                        description="Choose what to share and with whom. Create private collections, public tutorials, or password protected content for your community—your digital sanctuary for meaningful engagement."
                                        isRightAligned={true}
                                        colorClass="bg-emerald-500/10 text-emerald-400"
                                    />
                                </dl>
                            </div>
                        </div>
                    </div>

                    {/* Features Section */}
                    <div className="bg-neutral-950 bg-opacity-50 py-24 relative z-10">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <AnimatedBlockSection />
                        </div>
                    </div>
                    <AutofocusVisionSection />
                    <CreatorsSection usernames={['nate', 'autofocus', 'nate', 'autofocus', 'nate', 'autofocus', 'nate', 'autofocus', 'nate', 'autofocus', 'nate', 'autofocus']} />
                    <Footer />
                </div>
            </div>
        </>
    )
}

export default Home
